import { o as omitBy_1, i as isNil_1 } from './omitBy-d2a59910.js';
import { i as isEmpty_1 } from './isEmpty-ea893565.js';
import { p as pick_1 } from './pick-8e119733.js';
import './index-39f51a91.js';
import { f as formatString } from './index-67820103.js';
import { a as isProductUpdated } from './utils-e7f86918.js';
import { s as send, i as addToCartAlternative, h as addToCartSubstitution, r as removeFromFavoritesEvent } from './analytics-74fe4f64.js';

const NO_LETTERS_AND_LEADING_ZERO_REGEXP = /[^0-9]|^0{1}/g;
const NOT_SPECIFIED_PROPERTY_VAL = '—';
const RUNTIME_FIELD_MAX_LENGTH = 7;
const LOAD_PLACEHOLDER = '#load#';
const REQUIRED_RUNTIME_PARAMS = [
  'vah',
  'bcurveComp',
  'bcurveExpo',
  'bcurveSlope',
  'bcurveScale',
  'ptare',
];
const RUNTIME_ERROR_CODES = {
  EMPTY: 'errorEmpty',
  NULL_PARAM: 'errorHasNull',
  EXCEED_MAX_CAPACITY: 'errorExceedMaxCapacity',
  LESS_THAN_MINIMUM: 'errorLessThanMinimum',
};
const RUNTIME_LABEL_CODES = {
  PLEASE_ENTER_LOAD: 'pleaseEnterLoad',
  TOTAL_LOAD_EXCEEDS: 'totalLoadExceeds',
  ENTERED_LOAD_LESS_MINIMUM: 'enteredLoadLessMinimum',
  PLEASE_ENTER_LOAD_MAX_CAPACITY: 'pleaseEnterLoadMaxCapacity',
};
const RUNTIME_ERROR_CODES_TO_LABELS = {
  LOAD: {
    [RUNTIME_ERROR_CODES.EMPTY]: RUNTIME_LABEL_CODES.PLEASE_ENTER_LOAD,
    [RUNTIME_ERROR_CODES.EXCEED_MAX_CAPACITY]: RUNTIME_LABEL_CODES.TOTAL_LOAD_EXCEEDS,
    [RUNTIME_ERROR_CODES.LESS_THAN_MINIMUM]: RUNTIME_LABEL_CODES.ENTERED_LOAD_LESS_MINIMUM,
  },
  MAX_CAPACITY: {
    [RUNTIME_ERROR_CODES.EMPTY]: RUNTIME_LABEL_CODES.PLEASE_ENTER_LOAD_MAX_CAPACITY,
  },
};
const RUNTIME_FIELDS = {
  HOURS: 'hours',
  MINUTES: 'minutes',
  SECONDS: 'seconds',
};

function parseDecimal(value) {
  return parseInt(value, 10);
}
function calculateMaxCapacity(inputValue, pwrRated) {
  const value = parseDecimal(inputValue);
  return Math.round((value / pwrRated) * 100);
}
function calculateRuntimeMinutes(runtimeParams, calculatedValue) {
  const { vah, bcurveComp, bcurveExpo, bcurveSlope, bcurveScale, ptare } = runtimeParams;
  const mathPow = (vah / (calculatedValue + ptare)) ** bcurveExpo;
  return (60 * bcurveScale * (mathPow - bcurveSlope + (bcurveComp * (calculatedValue + ptare)) / vah));
}
function isMaxCapacityExceeded(value, pwrRated) {
  return parseDecimal(value) > pwrRated;
}
function isLoadLessThanMinimum(value, min) {
  return parseDecimal(value) < min;
}
function getMinValue(max, min) {
  return min !== null && min !== void 0 ? min : Math.ceil(max * 0.1);
}
function calculateRuntime(runtimeParams, value) {
  if (!value) {
    return null;
  }
  if (isMaxCapacityExceeded(value, runtimeParams.pwrRated)) {
    return null;
  }
  if (isLoadLessThanMinimum(value, getMinValue(runtimeParams.pwrRated, runtimeParams.startPoint))) {
    return null;
  }
  const runtime = calculateRuntimeMinutes(runtimeParams, parseDecimal(value));
  if (Number.isNaN(runtime) || runtime <= 0) {
    return null;
  }
  const resultRuntime = {};
  const runtimeMinutes = Math.floor(runtime);
  if (runtimeMinutes < 30) {
    resultRuntime.minutes = runtimeMinutes;
    resultRuntime.seconds = Math.floor((runtime - runtimeMinutes) * 60) || null;
  }
  if (runtimeMinutes >= 30 && runtimeMinutes < 60) {
    resultRuntime.minutes = runtimeMinutes;
  }
  if (runtimeMinutes >= 60) {
    resultRuntime.hours = Math.floor(runtimeMinutes / 60);
    resultRuntime.minutes = Math.floor(runtimeMinutes % 60) || null;
  }
  return omitBy_1(resultRuntime, isNil_1);
}
function stringifyRuntime(runtime, labels, defaultValue = NOT_SPECIFIED_PROPERTY_VAL) {
  if (isEmpty_1(runtime)) {
    return defaultValue;
  }
  return [RUNTIME_FIELDS.HOURS, RUNTIME_FIELDS.MINUTES, RUNTIME_FIELDS.SECONDS]
    .filter((field) => !isNil_1(runtime[field]))
    .map((field) => `${runtime[field]}${labels[field]}`)
    .join(' ');
}
function hasNullParams(runtimeParams) {
  const paramsValue = Object.values(pick_1(runtimeParams, REQUIRED_RUNTIME_PARAMS));
  return paramsValue.some(isNil_1);
}
function validate(runtimeParams, value) {
  const { pwrRated, startPoint } = runtimeParams !== null && runtimeParams !== void 0 ? runtimeParams : {};
  if (hasNullParams(runtimeParams)) {
    return RUNTIME_ERROR_CODES.NULL_PARAM;
  }
  if (!value) {
    return RUNTIME_ERROR_CODES.EMPTY;
  }
  if (isMaxCapacityExceeded(value, pwrRated)) {
    return RUNTIME_ERROR_CODES.EXCEED_MAX_CAPACITY;
  }
  if (isLoadLessThanMinimum(value, getMinValue(pwrRated, startPoint))) {
    return RUNTIME_ERROR_CODES.LESS_THAN_MINIMUM;
  }
  return null;
}
function formatMessage(message, labelCode, runtimeParams) {
  if (!message || !labelCode || isEmpty_1(runtimeParams)) {
    return message !== null && message !== void 0 ? message : null;
  }
  let load;
  const { pwrRated, startPoint } = runtimeParams;
  switch (labelCode) {
    case RUNTIME_LABEL_CODES.TOTAL_LOAD_EXCEEDS:
      load = pwrRated;
      break;
    case RUNTIME_LABEL_CODES.ENTERED_LOAD_LESS_MINIMUM:
      load = getMinValue(pwrRated, startPoint);
      break;
  }
  if (!load) {
    return message;
  }
  return formatString(message, {
    [LOAD_PLACEHOLDER]: load === null || load === void 0 ? void 0 : load.toString(),
  });
}

const buildCTAClickEvent = (initialEvent, options) => {
  const { isCartTransferEnabled, inCart, id, quantity, onFinish, groupId, groupType, displayName, } = options;
  const productCommonProps = {
    id,
    quantity,
  };
  const commonProps = {
    type: isCartTransferEnabled ? 'cart' : 'favorites',
    inList: isCartTransferEnabled ? false : inCart,
    initialEvent,
    onFinish,
  };
  if (groupId) {
    if (inCart && !isCartTransferEnabled) {
      return Object.assign(Object.assign({}, commonProps), { product: {
          id: groupId,
          repositoryId: groupId,
        } });
    }
    return Object.assign(Object.assign({}, commonProps), { product: Object.assign(Object.assign({}, productCommonProps), { groupId,
        groupType,
        displayName }) });
  }
  return Object.assign(Object.assign({}, commonProps), { product: Object.assign({}, productCommonProps) });
};
const getProducts = (response, productId, quantity) => {
  return Object.entries(response.infoForGa).map((entry) => {
    const [id, product] = entry;
    const { brand = '', description, price = null } = product;
    const isUpdated = isProductUpdated(id, response);
    return Object.assign({ brand,
      price,
      id, category: '', name: description, quantity: quantity[id] }, (isUpdated ? { variant: productId } : {}));
  });
};
const sendAnalytics = (response, productId, quantity, isAlternatives) => {
  var _a, _b;
  const { infoForGa } = response;
  const products = getProducts(response, productId, quantity);
  const currencyCode = (_b = (_a = Object.values(infoForGa).find((product) => product.currency)) === null || _a === void 0 ? void 0 : _a.currency) !== null && _b !== void 0 ? _b : '';
  if ('productsUpdated' in response) {
    send(isAlternatives ? addToCartAlternative : addToCartSubstitution, {
      currencyCode,
      products,
      distributor: '',
    });
  }
  else {
    send(removeFromFavoritesEvent, {
      currencyCode,
      products,
    });
  }
};

export { NOT_SPECIFIED_PROPERTY_VAL as N, RUNTIME_ERROR_CODES_TO_LABELS as R, NO_LETTERS_AND_LEADING_ZERO_REGEXP as a, buildCTAClickEvent as b, calculateMaxCapacity as c, RUNTIME_FIELD_MAX_LENGTH as d, calculateRuntime as e, formatMessage as f, getMinValue as g, stringifyRuntime as h, sendAnalytics as s, validate as v };
